import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react';

import * as locales from 'date-fns/locale';

import {Group} from '@pb/kit-component-group';
import {Tooltip} from '@pb/kit-component-popups';

import type {CalendarCoreProps} from './calendar-core';
import {CalendarCore} from './calendar-core';
import {CalendarInput} from './calendar-input';
import {defaultTodayButton} from './default-today-button';
import {CustomCalendarHeader} from './custom-calendar-header';
import {hasTimeFormat} from './utils/hasTimeFormat';
import {normalizeToLocalMidnight} from './utils/normalizeToLocalMidnight';
import {formatValidDateToString} from './utils/formatValidDateToString';
import {getDateRange} from './utils/getDateRange';
import {getEffectiveDateFormat} from './utils/getEffectiveDateFormat';

import type {ErrorType} from './constants';
import type {CustomHeaderProps} from './typedef';
import type {InputProps} from '@pb/kit-component-input-v2';
import type {Day, Locale} from 'date-fns';
import type {ComponentProps, ReactNode,
    ComponentType} from 'react';
import type DatePicker from 'react-datepicker';


export const DEFAULT_MIN_DATE: Date = new Date(1800, 0, 1);
export const DEFAULT_MAX_DATE: Date = new Date(2050, 11, 31);

const getLocaleObject = (localeName: string): Locale => {
    const locale = (locales as Record<string, Locale>)[localeName];
    if (!locale) {
        return locales.enUS;
    }
    return locale;
};

// Date-only mode uses local midnight. UTC midnight incoming dates are converted.
// DateTime mode preserves exact local time — isDateOnlyMode guard enforces this.
const normalizeMidnightDate = <T extends Date | null | undefined>(
    date: T,
    isDateOnlyMode: boolean,
): T => {
    if (!date || !isDateOnlyMode || Number.isNaN(date.getTime())) {
        return date;
    }
    return normalizeToLocalMidnight(date) as T;
};

export interface OmittedInputProps extends Omit<InputProps, 'value'> {
    value?: string,
}

type DatePickerProps = Omit<ComponentProps<typeof DatePicker>, 'onChange' | 'locale' | 'onCalendarClose'>;

export interface CalendarProps extends DatePickerProps {
    /**
     * The `Date` value that is set when the user clicks the clear (×) button.
     * When omitted, the clear button resets the input to `null` / `undefined`.
     */
    clearableValue?: Date,
    /**
     * Custom React component to replace the default month/year navigation header.
     * Receives `CustomHeaderProps` (month, year, navigation callbacks, etc.) so
     * you can build a fully bespoke header while keeping the picker open.
     */
    CustomHeader?: ComponentType<CustomHeaderProps>,
    /**
     * Date format string following date-fns format patterns.
     * Common examples: `"MM-dd-yyyy"`, `"dd-MM-yyyy"`, `"yyyy-MM-dd"`, `"dd-MMM-yyyy"`, `"PPP"`.
     * Defaults to `"MM-dd-yyyy"`.
     * @see {@link https://date-fns.org/docs/format date-fns format documentation}
     */
    dateFormat?: CalendarCoreProps['dateFormatCalendar'],
    /**
     * A date-fns locale key (e.g. `"enUS"`, `"enGB"`, `"de"`) that controls day/month
     * names and the first day of the week shown in the picker. Defaults to `"enUS"`.
     */
    locale?: string,
    /**
     * Called just before the calendar popover closes. Receives the currently selected
     * `Date` (or `null` if cleared) so you can trigger validation or API calls at
     * close time rather than on every selection.
     */
    onCalendarClose?: (date: Date | null) => void,
    /**
     * Called when the user clicks a day cell or finishes typing a complete, valid date.
     * Receives the normalized `Date`. Not called while the user is mid-typing or on clear
     * (use `isClearable` + `clearableValue` for clear behavior). Use `onChangeRange`
     * instead for range or week-picker modes.
     */
    onChange?: (date?: Date) => void,
    /**
     * Called when the user selects a date range or week. Receives a two-element array
     * `[startDate, endDate]` (each `Date | undefined`). Use together with
     * `selectsRange` or `showWeekPicker`.
     */
    onChangeRange?: (range: Array<Date | undefined>) => void,
    /**
     * Extra props forwarded to the internal `<Input>` trigger. Use to set `label`,
     * `placeholder`, `disabled`, `error`, or other input-level props without
     * needing to replace the entire input.
     */
    inputProps?: OmittedInputProps,
    /**
     * Called whenever the typed or selected date changes. Receives the error type
     * key (`ErrorType`) and a human-readable error message when the date is invalid,
     * or `undefined`/`undefined` when the value is valid. Pair with
     * `<FieldDescription>` to display inline validation feedback.
     * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/forms-form-fields-with-error--documentation}
     */
    onValidationUpdate?: (errorType?: ErrorType, errorMessage?: string) => void,
    /**
     * When `true`, wraps the picker in a `<Tooltip>` that shows the configured
     * `minDate` and `maxDate` boundaries when the user hovers over the input.
     * Useful to communicate allowed date ranges without requiring extra UI.
     */
    withRangeHint?: boolean,
    /**
     * Custom node to render as the "Today" shortcut button inside the picker.
     * When omitted, a default "Today" button is rendered that selects today's
     * date (only shown when today falls within `minDate`–`maxDate`).
     */
    todayButton?: ReactNode,
}

/**
 * Full-featured date picker supporting single dates, date ranges, and week selection.
 *
 * Wraps `react-datepicker` with a design-system input (`CalendarInput`), a custom
 * month/year navigation header (`CustomCalendarHeader`), and a "Today" button.
 * Incoming `Date` objects are normalized to local midnight in date-only mode so that
 * UTC midnight values from APIs always display on the correct calendar day.
 *
 * Pass `onChange` for single date selection or `onChangeRange` for range and week
 * modes. Use `dateFormat` (date-fns format string, default `"MM-dd-yyyy"`) to
 * control how the selected date is displayed in the input. Set `locale` to a
 * date-fns locale key (e.g. `"enGB"`) to localize day/month names and the first
 * day of the week. When `showTimeInput={true}`, a time toggle appears that extends
 * the format to include hours and minutes.
 *
 * Supply `onValidationUpdate` to receive typed errors (`ErrorType`) and plain-text
 * error messages when the user types an invalid date — pair this with a
 * `FieldDescription` component to show inline validation feedback.
 *
 * `withRangeHint` wraps the picker in a `Tooltip` that shows the configured
 * `minDate` / `maxDate` boundary on hover.
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/story/forms-calendar--calendar-default}
 */

export const Calendar = forwardRef<DatePicker, CalendarProps>(
    (props, ref) => {
        const {
            CustomHeader,
            clearableValue,
            dateFormat = 'MM-dd-yyyy',
            endDate: endDateProp,
            inline = false,
            inputProps,
            isClearable = false,
            locale = 'enUS',
            maxDate = DEFAULT_MAX_DATE,
            minDate = DEFAULT_MIN_DATE,
            onCalendarClose,
            onChange,
            onChangeRange,
            onKeyDown,
            popperPlacement = 'bottom-end',
            selected: selectedProp = null,
            onValidationUpdate,
            showWeekPicker = false,
            startDate: startDateProp,
            withRangeHint = false,
            todayButton,
            showTimeInput = false,
            ...calendarProps
        } = props;
        const [includeTime, setIncludeTime] = useState(false);

        // Determine which format to use for CalendarCore and input placeholder.
        const effectiveDateFormat = useMemo(
            () => getEffectiveDateFormat(showTimeInput && includeTime, dateFormat),
            [showTimeInput, includeTime, dateFormat],
        );
        const effectiveDateFormatString = typeof effectiveDateFormat === 'string'
            ? effectiveDateFormat
            : effectiveDateFormat?.[0] ?? '';
        const isTimeMode = hasTimeFormat(effectiveDateFormatString);
        const isDateOnlyMode = !isTimeMode;

        const normalizedClearableValue = useMemo(
            () => normalizeMidnightDate(clearableValue, isDateOnlyMode) ?? clearableValue,
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [clearableValue?.toString(), isDateOnlyMode],
        );
        const normalizedMinDate = useMemo(
            () => {
                return normalizeMidnightDate(minDate, isDateOnlyMode) ?? minDate;
            },
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [minDate?.toString(), isDateOnlyMode],
        );
        const normalizedMaxDate = useMemo(
            () => {
                return normalizeMidnightDate(maxDate, isDateOnlyMode);
            },
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [maxDate?.toString(), isDateOnlyMode],
        );
        const normalizedSelectedProp = useMemo(
            () => normalizeMidnightDate(selectedProp, isDateOnlyMode),
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [selectedProp?.toString(), isDateOnlyMode],
        );
        const normalizedStartDateProp = useMemo(
            () => normalizeMidnightDate(startDateProp, isDateOnlyMode),
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [startDateProp?.toString(), isDateOnlyMode],
        );
        const normalizedEndDateProp = useMemo(
            () => normalizeMidnightDate(endDateProp, isDateOnlyMode),
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [endDateProp?.toString(), isDateOnlyMode],
        );

        const [selected, setSelected] = useState<Date | null>(normalizedSelectedProp);
        const [startDate, setStartDate] = useState<Date | undefined | null>(normalizedStartDateProp);
        const [endDate, setEndDate] = useState<Date | undefined | null>(normalizedEndDateProp);

        const inputRef = useRef<HTMLInputElement | null>(null);
        const calendarRef = useRef<DatePicker | null>(null);

        useImperativeHandle(ref, () => calendarRef.current as DatePicker, []);

        useEffect(() => {
            setSelected(normalizedSelectedProp);
        }, [normalizedSelectedProp]);
        useEffect(() => {
            setStartDate(normalizedStartDateProp);
        }, [normalizedStartDateProp]);
        useEffect(() => {
            setEndDate(normalizedEndDateProp);
        }, [normalizedEndDateProp]);
        useEffect(() => {
            if (!showTimeInput && includeTime) {
                setIncludeTime(false);
            }
        }, [showTimeInput, includeTime]);

        const localObject = getLocaleObject(locale);
        const weekStartsOn: Day = localObject.options?.weekStartsOn || 0;


        const handleChange = (selectedDate: Date | Date[] | null, event?: React.SyntheticEvent) => {
            const isArray = Array.isArray(selectedDate);
            if (!selectedDate || (isArray && selectedDate.every(date => date === null))) {
                onClear();
                return;
            }

            if (showWeekPicker && !isArray) {
                const normalizedSelectedDate = normalizeMidnightDate(selectedDate, isDateOnlyMode);
                const dateRange = getDateRange(normalizedSelectedDate, weekStartsOn, dateFormat, true);
                onChangeRange?.(dateRange);
                setSelected(dateRange[0]);
            } else if (isArray) {
                const [startingDate, endingDate] = selectedDate.map(
                    date => normalizeMidnightDate(date, isDateOnlyMode),
                );
                setStartDate(startingDate);
                setEndDate(endingDate);
                onChangeRange?.([startingDate, endingDate]);
            } else if (!isArray) {
                const normalizedSelectedDate = normalizeMidnightDate(selectedDate, isDateOnlyMode);
                setSelected(normalizedSelectedDate);
                if (event?.type === 'change' && isDateOnlyMode) {
                    const typedValue = (event as React.ChangeEvent<HTMLInputElement>).target?.value ?? '';
                    if (formatValidDateToString(normalizedSelectedDate, effectiveDateFormatString) === typedValue) {
                        onChange?.(normalizedSelectedDate);
                    }
                } else {
                    onChange?.(normalizedSelectedDate);
                }
            }
        };

        const handleCustomHeader = (customHeaderProps: CustomHeaderProps) => {
            const decreaseMonth = () => {
                customHeaderProps.decreaseMonth();
            };

            const increaseMonth = () => {
                customHeaderProps.increaseMonth();
            };

            const HeaderComponent = CustomHeader || CustomCalendarHeader;

            return (
                <HeaderComponent
                    {...customHeaderProps}
                    decreaseMonth={decreaseMonth}
                    increaseMonth={increaseMonth}
                    minDate={normalizedMinDate}
                    maxDate={normalizedMaxDate}
                    selectDate={handleChange}
                    selectedDate={selected}
                />
            );
        };

        const onClear = () => {
            if (calendarProps?.readOnly) {
                return;
            }
            setSelected(normalizedClearableValue ?? null);
            onChange?.(normalizedClearableValue ?? undefined);
            onChangeRange?.([undefined, undefined]);
            setStartDate(undefined);
            setEndDate(undefined);
        };

        const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
            onKeyDown?.(event);
            if (event.key === 'Escape') {
                // Workaround to fix the closing of the Dropdown on "Escape" key press
                event.stopPropagation();
            }
        };

        const calendarInput = (
            <CalendarInput
                {...inputProps}
                ref={inputRef}
                clearableValue={normalizedClearableValue ?? undefined}
                dateFormat={effectiveDateFormat ?? dateFormat}
                isClearable={isClearable}
                maxDate={normalizedMaxDate}
                minDate={normalizedMinDate}
                onClear={onClear}
                onDateChange={handleChange}
                onValidationUpdate={onValidationUpdate}
                showWeekPicker={showWeekPicker}
                weekStartsOn={weekStartsOn}
            />
        );

        // Update placeholder if we're using dateTimeFormat
        const effectivePlaceholder = useMemo(() => {
            if (props.placeholderText) {
                return props.placeholderText;
            }

            return effectiveDateFormat?.toUpperCase() ?? '';
        }, [props.placeholderText, effectiveDateFormat]);

        const calendarCore = (
            <CalendarCore
                {...calendarProps}
                ref={calendarRef}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                {...(props.showMonthYearDropdown && ({showMonthYearDropdown: true} as any))}
                customInput={calendarInput}
                dateFormat={effectiveDateFormat}
                endDate={endDate}
                inline={inline}
                locale={localObject}
                maxDate={normalizedMaxDate}
                minDate={normalizedMinDate}
                onCalendarClose={() => onCalendarClose?.(selected)}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                placeholderText={effectivePlaceholder}
                popperPlacement={popperPlacement}
                showTimeInput={showTimeInput}
                includeTime={includeTime}
                onIncludeTimeChange={setIncludeTime}
                renderCustomHeader={handleCustomHeader}
                selected={selected}
                showWeekPicker={showWeekPicker}
                startDate={startDate}
                todayButton={todayButton ?? defaultTodayButton({
                    selected,
                    maxDate: normalizedMaxDate,
                    minDate: normalizedMinDate,
                })}
            />
        );

        const tooltipCaption = (
            <Group justify="space-between">
                <div>
                    <strong>Min Date:</strong> {formatValidDateToString(normalizedMinDate, dateFormat)};
                </div>
                <div>
                    <strong>Max Date:</strong> {formatValidDateToString(normalizedMaxDate, dateFormat)}
                </div>
            </Group>
        );

        return (
            <>
                {withRangeHint ? (
                    <Tooltip
                        anchor={({anchorRef, getAnchorProps}) => (
                            <span
                                ref={anchorRef}
                                {...getAnchorProps()}>{calendarCore}
                            </span>
                        )}
                        placement="right">
                        {tooltipCaption}
                    </Tooltip>
                ) : (
                    calendarCore
                )}
            </>
        );
    },
);

Calendar.displayName = 'Calendar';
